import ContentLoader from 'react-content-loader'
import styled, { useTheme } from 'styled-components'

import { IconInfo } from 'components/Icons/InfoIcon'
import { Typography } from 'components/Typography/Typography'
import { useIsMobile } from 'utils/useIsMobile'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { ApyTooltip, BalanceTooltip, EarnedTooltip } from './Tooltips'
import { getPctValue } from 'utils/pctFormatter'

const LabelWithTooltip = styled(Typography)`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-bottom: 12px;
  }
`

const DetailsInfoContainerItem = styled.div`
  padding: 24px;
  border-radius: 16px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.background.primary};
  box-shadow: 0px 0px 16px 0px ${p => p.theme.colors.background.hover};
`

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 16px;
  align-items: center;
  border-bottom: 1px solid ${p => p.theme.colors.text.primary};
`

const OverviewRow = styled.div`
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

const OverviewItem = styled.div`
  box-sizing: border-box;
  min-width: 0;

  .centerSvg {
    > div {
      display: flex;
      cursor: pointer;
    }
  }

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex: 1 1 calc(50% - 8px);
  }
`

const OverviewItemSmall = styled(OverviewItem)`
  flex: 0 0 calc(31% - 9.92px);
`

const OverviewItemLarge = styled(OverviewItem)`
  flex: 0 0 calc(38% - 12.16px);
`

const DepositedValueContainer = styled.div`
  display: flex;
  > * {
    &:first-child {
      margin-right: 6px;
    }
    &:nth-child(2) {
      color: ${p => p.theme.colors.text.description};
    }
  }
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
    > * {
      &:first-child {
        margin-right: 0px;
        margin-bottom: 4px;
      }
    }
  }
`

const ApyAvgContainer = styled.div`
  display: flex;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
  }
`

interface Props {
  equity?: string
  deposited?: string
  earned?: string
  apy?: number
  apy7d?: number
  apy30d?: number
  tvl?: string
  realizedProfit?: string
  unrealizedProfit?: string
  showDepositedLoader?: boolean
  performanceFee?: string
  earnedUsd?: string
  equityUsd?: string
  tvlUsd?: string
  tooltipFormattedEarned?: string
  tooltipFormattedEquity?: string
  strategyYieldRatio24h?: {
    [x: string]: number
  }
  isLoadingApyAvg?: boolean
}

export const DetailsContentLoading = () => {
  const theme = useTheme()

  return (
    <ContentLoader
      speed={2}
      width={150}
      height={25}
      viewBox="0 0 150 25"
      backgroundColor={theme.colors.contentLoader.backgroundColor}
      foregroundColor={theme.colors.contentLoader.foregroundColor}
    >
      <rect x="0" y="8" rx="3" ry="3" width="75" height="7" />
      <rect x="0" y="26" rx="3" ry="3" width="150" height="7" />
    </ContentLoader>
  )
}

export const SubDetailsContentLoading = () => {
  const theme = useTheme()

  return (
    <ContentLoader
      speed={2}
      width={85}
      height={25}
      viewBox="0 0 85 25"
      backgroundColor={theme.colors.contentLoader.backgroundColor}
      foregroundColor={theme.colors.contentLoader.foregroundColor}
    >
      <rect x="0" y="8" rx="3" ry="3" width="85" height="7" />
      <rect x="0" y="26" rx="3" ry="3" width="25" height="7" />
    </ContentLoader>
  )
}

export const AvgApy = ({
  formatted7dApyAvg,
  formatted30dApyAvg,
  isLoading,
}: {
  formatted7dApyAvg: string
  formatted30dApyAvg: string
  isLoading?: boolean
}) => (
  <ApyAvgContainer>
    <div style={{ display: 'flex', alignItems: 'center', marginRight: 12 }}>
      {isLoading ? (
        <SubDetailsContentLoading />
      ) : (
        <>
          <Typography variant="caption" color="secondary">
            7D AVG:
          </Typography>
          <Typography variant="body" color="secondary" fontWeight="bold" style={{ marginLeft: 4 }}>
            {formatted7dApyAvg || '-'}
          </Typography>
        </>
      )}
    </div>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {isLoading ? (
        <SubDetailsContentLoading />
      ) : (
        <>
          <Typography variant="caption" color="secondary">
            30D AVG:
          </Typography>
          <Typography variant="body" color="secondary" fontWeight="bold" style={{ marginLeft: 4 }}>
            {formatted30dApyAvg || '-'}
          </Typography>
        </>
      )}
    </div>
  </ApyAvgContainer>
)
export const Overview = ({
  equity,
  deposited,
  earned,
  apy,
  apy7d,
  apy30d,
  tvl,
  tvlUsd,
  showDepositedLoader,
  performanceFee,
  strategyYieldRatio24h,
  realizedProfit,
  unrealizedProfit,
  earnedUsd,
  equityUsd,
  tooltipFormattedEarned,
  tooltipFormattedEquity,
  isLoadingApyAvg,
}: Props) => {
  const isMobile = useIsMobile()

  const estApy = apy !== undefined ? (apy * 100).toFixed(2) + '%' : ''

  const formatted7dApyAvg = apy7d !== undefined ? getPctValue(apy7d) : ''
  const formatted30dApyAvg = apy30d !== undefined ? getPctValue(apy30d) : ''

  return (
    <DetailsInfoContainerItem>
      <Title>
        <Typography variant={isMobile ? 'caption' : 'label1'} fontWeight="semiBold">
          Overview
        </Typography>
      </Title>
      {isMobile ? (
        <>
          <OverviewRow>
            <OverviewItem>
              <Typography variant="caption" color="secondary" style={{ marginBottom: 12 }}>
                Total Value Locked
              </Typography>
              <Typography variant="description" fontWeight="bold">
                {tvl ? (
                  <div>
                    <div>{tvl}</div>
                    <Typography variant="caption" color="secondary">
                      {tvlUsd}
                    </Typography>
                  </div>
                ) : (
                  <DetailsContentLoading />
                )}
              </Typography>
            </OverviewItem>
            <OverviewItem>
              <LabelWithTooltip variant="caption" color="secondary" className="centerSvg" style={{ marginBottom: 9 }}>
                APY
                <ApyTooltip apy={apy} strategyYieldRatio24h={strategyYieldRatio24h} />
              </LabelWithTooltip>
              <Typography variant="description" fontWeight="bold">
                {estApy ? estApy : <DetailsContentLoading />}
                <AvgApy
                  formatted30dApyAvg={formatted30dApyAvg}
                  formatted7dApyAvg={formatted7dApyAvg}
                  isLoading={isLoadingApyAvg}
                />
              </Typography>
            </OverviewItem>
          </OverviewRow>
          <OverviewRow>
            <OverviewItem>
              <LabelWithTooltip variant="caption" color="secondary" className="centerSvg">
                Earned
                <EarnedTooltip
                  realizedProfit={realizedProfit}
                  unrealizedProfit={unrealizedProfit}
                  earned={tooltipFormattedEarned}
                />
              </LabelWithTooltip>
              <Typography variant="description" fontWeight="bold">
                {earned ? (
                  <div>
                    <div>{earned}</div>
                    <Typography variant="caption" color="secondary">
                      {earnedUsd}
                    </Typography>
                  </div>
                ) : (
                  <DetailsContentLoading />
                )}
              </Typography>
            </OverviewItem>
            <OverviewItem>
              <LabelWithTooltip variant="caption" color="secondary" className="centerSvg">
                Balance
                <BalanceTooltip
                  equity={tooltipFormattedEquity}
                  deposited={deposited}
                  unrealizedProfit={unrealizedProfit}
                />
              </LabelWithTooltip>
              <Typography variant="description" fontWeight="bold">
                {showDepositedLoader ? (
                  <DetailsContentLoading />
                ) : (
                  <DepositedValueContainer>
                    <div>
                      <div>{equity}</div>
                      <Typography variant="caption" color="secondary">
                        {equityUsd}
                      </Typography>
                    </div>
                  </DepositedValueContainer>
                )}
              </Typography>
            </OverviewItem>
          </OverviewRow>
          <OverviewRow>
            <OverviewItem>
              <Typography variant="caption" color="secondary" style={{ marginBottom: 12 }}>
                Performance fee
              </Typography>
              <Typography variant="description" fontWeight="bold">
                {performanceFee}
              </Typography>
            </OverviewItem>
          </OverviewRow>
        </>
      ) : (
        <>
          <OverviewRow>
            <OverviewItemSmall>
              <Typography variant="body" color="secondary" style={{ marginBottom: 16 }}>
                Total Value Locked
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold">
                {tvl ? (
                  <div>
                    <div>{tvl}</div>
                    <Typography variant="body" color="secondary">
                      {tvlUsd}
                    </Typography>
                  </div>
                ) : (
                  <DetailsContentLoading />
                )}
              </Typography>
            </OverviewItemSmall>
            <OverviewItemLarge>
              <LabelWithTooltip variant="body" color="secondary" className="centerSvg">
                APY
                <ApyTooltip apy={apy} strategyYieldRatio24h={strategyYieldRatio24h} />
              </LabelWithTooltip>
              <Typography variant="subtitle2" fontWeight="bold">
                <div>
                  <div>{estApy ? estApy : <DetailsContentLoading />}</div>
                  <AvgApy
                    formatted30dApyAvg={formatted30dApyAvg}
                    formatted7dApyAvg={formatted7dApyAvg}
                    isLoading={isLoadingApyAvg}
                  />
                </div>
              </Typography>
            </OverviewItemLarge>
            <OverviewItemSmall>
              <LabelWithTooltip variant="body" color="secondary" className="centerSvg">
                Performance fee
                <Tooltip
                  content="A small fee deducted from the profits generated by your investments, used to support platform operations."
                  position="top"
                >
                  <IconInfo width={16} height={16} style={{ marginLeft: 6 }} />
                </Tooltip>
              </LabelWithTooltip>
              <Typography variant="subtitle2" fontWeight="bold">
                {performanceFee}
              </Typography>
            </OverviewItemSmall>
          </OverviewRow>
          <OverviewRow>
            <OverviewItemSmall>
              <LabelWithTooltip variant="body" color="secondary" className="centerSvg">
                Earned
                <EarnedTooltip
                  realizedProfit={realizedProfit}
                  unrealizedProfit={unrealizedProfit}
                  earned={tooltipFormattedEarned}
                />
              </LabelWithTooltip>
              <Typography variant="subtitle2" fontWeight="bold">
                {earned ? (
                  <div>
                    <div>{earned}</div>
                    <Typography variant="body" color="secondary">
                      {earnedUsd}
                    </Typography>
                  </div>
                ) : (
                  <DetailsContentLoading />
                )}
              </Typography>
            </OverviewItemSmall>
            <OverviewItemLarge>
              <LabelWithTooltip variant="body" color="secondary" className="centerSvg">
                Balance
                <BalanceTooltip
                  equity={tooltipFormattedEquity}
                  deposited={deposited}
                  unrealizedProfit={unrealizedProfit}
                />
              </LabelWithTooltip>
              <Typography variant="subtitle2" fontWeight="bold">
                {showDepositedLoader ? (
                  <DetailsContentLoading />
                ) : (
                  <DepositedValueContainer>
                    <div>
                      <div>{equity}</div>
                      <Typography variant="body" color="secondary">
                        {equityUsd}
                      </Typography>
                    </div>
                  </DepositedValueContainer>
                )}
              </Typography>
            </OverviewItemLarge>
          </OverviewRow>
        </>
      )}
    </DetailsInfoContainerItem>
  )
}
